import jQuery from 'jquery';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Date,
  Price,
  TableAlternate,
} from 'components';

import fetchExternalData from 'utils/fetch-external-data';
import { getLocationParam } from 'utils/get-query-param';
import messages from 'utils/intl-messages';

export default class QuoteView extends Component {

  /* Lifecycle */

  constructor(props) {
    super(props);

    this.SYSTEM_COLUMNS = [
      {
        accessor: 'name',
        header: props.intl.messages['systems.quote.columns.name'],
      }, {
        accessor: 'sap',
        header: props.intl.messages['systems.quote.columns.serial_number'],
        modifier: 'shrink',
      }, {
        accessor: 'amount',
        header: props.intl.messages['systems.quote.columns.amount'],
        modifier: 'shrink',
      }, {
        accessor: 'price',
        cell: row => this.renderPrice(row.price),
        header: props.intl.messages['systems.quote.columns.price'],
        modifier: 'shrink',
      }, {
        accessor: 'vat_percentage',
        header: props.intl.messages['systems.quote.columns.vat_percentage'],
        modifier: 'shrink',
      }, {
        accessor: 'vat_total',
        cell: row => this.renderPrice(row.vat_total),
        header: props.intl.messages['systems.quote.columns.vat_total'],
        modifier: 'shrink',
      }, {
        accessor: 'subtotal',
        cell: row => this.renderPrice(row.subtotal),
        header: props.intl.messages['systems.quote.columns.subtotal'],
        modifier: 'shrink',
      },
    ];

    const locale = getLocationParam('locale', 'nl', props.location);

    if (locale !== 'nl') {
      props.changeLanguage({
        locale,
        messages: messages(locale),
      });
    }

    this.state = {
      projectInfo: null,
      projectProducts: null,
      ready: false,
      tableSystem: [],
    };
  }

  componentWillMount() {
    // Add body modifier for exporting
    jQuery('body').addClass('printable');

    const { params: { projectId } } = this.props;

    const endpoints = [
      `/api/projects/${projectId}`,
      `/api/projects/${projectId}/products`,
    ];

    fetchExternalData(endpoints).then((result) => {
      const projectInfo = result[0].json;
      const projectProducts = result[1].json;

      this.setState({
        projectInfo,
        projectProducts,
      }, () => {
        this.formatProductsData(projectProducts);
      });
    });
  }

  componentWillUnmount() {
    // Remove body modifier for exporting
    jQuery('body').removeClass('printable');
  }

  /* Methods */

  calculateTableSubtotal(table) {
    let result = 0;

    if (table.length) {
      table.forEach((row) => {
        if (!row.exclude_price) {
          result += row.subtotal;
        }
      });
    }

    result = Math.round(result * 100) / 100;

    return result;
  }

  calculateTotal() {
    const { projectProducts } = this.state;

    let subtotalInclVAT = 0;
    let totalVAT = 0;

    if (projectProducts) {
      projectProducts.data.forEach((item) => {
        const product = item.attributes;

        if (!product.exclude_price && product.slot !== 'personal-costs' && product.slot !== 'discounts') {
          totalVAT += product.vat_total;
          subtotalInclVAT += product.subtotal;
        }
      });
    }

    return {
      subtotalExclVAT: subtotalInclVAT - totalVAT,
      subtotalInclVAT,
      totalAll: subtotalInclVAT,
      totalVAT,
    };
  }

  formatProductsData(products) {
    const tableSystem = [];

    if (products) {
      products.data.forEach((product) => {
        if (!product.attributes.exclude_price) {
          const newProduct = {
            ...product.attributes,
            id: product.id,
          };

          if (product.attributes.slot !== 'personal-costs' && product.attributes.slot !== 'discounts') {
            tableSystem.push(newProduct);
          }
        }
      });
    }

    this.setState({
      ready: true,
      tableSystem,
    });
  }

  /* Render */

  render() {
    const { ready } = this.state;

    if (!ready) {
      return (
        <div className="quote-view">
          <FormattedMessage id="systems.share.loading" />
        </div>
      );
    }

    return (
      <div className="quote-view">
        <div className="offset">{this.renderHeader()}</div>
        <div className="offset offset--md">{this.renderInfoBar()}</div>
        <div className="offset offset--md page-break-avoid">
          <div className="quote-view__value quote-view__value--spacing">
            <FormattedMessage id="systems.quote.system" />
          </div>
          {this.renderSystemInfo()}
        </div>
        <div className="offset offset--md page-break-avoid">{this.renderCalculationTotal()}</div>
      </div>
    );
  }

  renderHeader() {
    return (
      <div className="align align--fluid align--gutter-sm">
        <div className="align__cell align__cell--shrink">
          <h1 className="quote-view__title">
            <FormattedMessage id="systems.quote.title" />
          </h1>
        </div>
      </div>
    );
  }

  renderInfoBar() {
    const { projectInfo: { data: { attributes } } } = this.state;
    const today = moment();

    return (
      <div className="quote-view__bar">
        <div className="quote-view__bar-item">
          <div className="quote-view__label">
            <FormattedMessage id="systems.share.date" />
          </div>
          <div className="quote-view__value">
            <Date value={today} />
          </div>
        </div>
        <div className="quote-view__bar-item">
          <div className="quote-view__label">
            <FormattedMessage id="systems.share.totalAmount" />
          </div>
          <div className="quote-view__value">
            <Price value={attributes.price} />
          </div>
        </div>
      </div>
    );
  }

  renderSystemInfo() {
    const { tableSystem } = this.state;
    const subtotal = this.calculateTableSubtotal(tableSystem);

    return (
      <TableAlternate
        columns={this.SYSTEM_COLUMNS}
        rows={tableSystem}
        subtotal={subtotal}
        includesVat
      />
    );
  }

  renderCalculationTotal() {
    const calculation = this.calculateTotal();

    return (
      <div className="align align--fluid">
        <div className="align__cell align__cell" />
        <div className="align__cell align__cell--shrink">
          <div className="quote-view__calculation">
            <div className="align align--fluid align--gutter-sm align--v-gutter-sm">
              <div className="align__row">
                <div className="align__cell">
                  <FormattedMessage id="systems.quote.calculation.subtotalExclVAT" />
                </div>
                <div className="align__cell u-text-right"><Price value={calculation.subtotalExclVAT} /></div>
              </div>
              <div className="align__row">
                <div className="align__cell">
                  <FormattedMessage id="systems.quote.calculation.vat" />
                </div>
                <div className="align__cell u-text-right"><Price value={calculation.totalVAT} /></div>
              </div>
              <div className="align__row">
                <div className="align__cell">
                  <FormattedMessage id="systems.quote.calculation.subtotalInclVAT" />
                </div>
                <div className="align__cell u-text-right"><Price value={calculation.subtotalInclVAT} /></div>
              </div>
              <div className="align__row quote__total">
                <div className="align__cell">
                  <FormattedMessage id="systems.quote.calculation.total" />
                </div>
                <div className="align__cell u-text-right"><Price value={calculation.totalAll} /></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPrice(price) {
    return (
      <div className="table-alternate-body__content table-alternate-body__content--align-right">
        <Price value={price} />
      </div>
    );
  }
}

QuoteView.propTypes = {
  changeLanguage: PropTypes.func,
  intl: PropTypes.object,
  params: PropTypes.object,
  location: PropTypes.object,
};



// WEBPACK FOOTER //
// ./js/views/quote-view/index.jsx